import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, Tag } from "lucide-react";
import { API, useApp } from "../context";
import { isPromptFree } from "../lib/promptFree";

// One-tap discount shown the moment a free premium preview ends (fired by the
// "iw:preview-ended" event from PreviewExpiryWatcher). Fully admin-controlled
// from the Premium Preview tool — OFF/empty by default, so nothing shows until
// an offer is turned on. Built now so a bigger future discount is one toggle away.
export default function PostTrialOfferModal() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);

  const onEnded = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/premium-preview/display-config`);
      const o = data?.post_trial_offer;
      if (o && o.enabled) setOffer(o);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    window.addEventListener("iw:preview-ended", onEnded);
    return () => window.removeEventListener("iw:preview-ended", onEnded);
  }, [onEnded]);

  if (isPromptFree()) return null; // free-premium window: no upgrade prompts
  if (!user?.id || !offer) return null;

  const go = () => {
    const url = (offer.url || "").trim();
    setOffer(null);
    if (url.startsWith("http")) { window.location.href = url; return; }
    navigate(url && url.startsWith("/") ? url : "/subscribe");
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="post-trial-offer-modal">
      <div className="absolute inset-0 bg-black/70" onClick={() => setOffer(null)} />
      <div
        className="relative w-full max-w-sm rounded-3xl border-2 border-amber-400/40 p-6 text-center"
        style={{ background: "linear-gradient(180deg, rgba(30,16,6,0.98), rgba(40,20,30,0.98))", boxShadow: "0 0 80px rgba(245,158,11,0.3)" }}
      >
        <button onClick={() => setOffer(null)} className="absolute top-3 right-3 text-white/40 hover:text-white" data-testid="post-trial-offer-close">
          <X size={18} />
        </button>
        <div className="mx-auto mb-3 w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center">
          <Sparkles className="text-amber-300" size={28} />
        </div>
        <h3 className="text-white font-black text-xl mb-1" data-testid="post-trial-offer-headline">
          {offer.headline || "Keep your premium for less"}
        </h3>
        <p className="text-white/70 text-sm mb-4" data-testid="post-trial-offer-body">
          {offer.body || "Your free premium just ended. Subscribe now and lock in a special rate."}
        </p>
        {offer.code ? (
          <div className="mb-4 inline-flex items-center gap-2 rounded-xl border border-amber-400/40 bg-amber-500/10 px-4 py-2" data-testid="post-trial-offer-code">
            <Tag size={14} className="text-amber-300" />
            <span className="text-amber-200 font-black tracking-widest">{offer.code}</span>
          </div>
        ) : null}
        <button
          onClick={go}
          data-testid="post-trial-offer-cta"
          className="w-full py-3 rounded-xl text-amber-950 font-black text-sm"
          style={{ background: "linear-gradient(135deg,#f59e0b,#fcd34d)" }}
        >
          {offer.cta_label || "See the offer"}
        </button>
      </div>
    </div>
  );
}
